import cx from 'classnames';
import type { FunctionComponent } from 'react';

import { defineSlotComponent, findAllForSlots, findFirstForSlot } from './slots';

import styles from './layouts.module.less';

type SidebarLayoutProps = {
  //  This defaults to true, because typically we want that spacing at the top of page,
  //  but occasionally when SidebarLayouts are used in the middle of the page, we don't want that.
  hasPaddingTop?: boolean;
};

type SidebarLayoutComponent = FunctionComponent<SidebarLayoutProps> & {
  Header: FunctionComponent;
  Main: FunctionComponent;
  Sidebar: FunctionComponent;
};

/**
 * SidebarLayout is a two-column page layout.
 * Used to when there is main content, such as a list of data,
 * as well as a sidebar with actions/filters for that data.
 * Note that SidebarLayout will only render children wrapped in it's
 * sub-components, and only the first of each. The order of
 * Sidebar and Main will be preserved.
 *
 * @example
 * <SidebarLayout>
 *   <SidebarLayout.Header>
 *     <Header />
 *   </SidebarLayout.Header>
 *   <SidebarLayout.Main>
 *     <Inbox />
 *   </SidebarLayout.Main>
 *   <SidebarLayout.Sidebar>
 *     <Filters />
 *   </SidebarLayout.Sidebar>
 * </SidebarLayout>
 */
const SidebarLayout: SidebarLayoutComponent = ({ children, hasPaddingTop = true }) => {
  const header = findFirstForSlot('header', children);
  // Use findAllForSlots to persist the order of elements as provided
  const mainAndSidebar = findAllForSlots(['main', 'sidebar'], children);

  return (
    <main className={cx(styles.sidebarLayout, {
      [styles.sidebarLayoutPadded]: hasPaddingTop,
    })}>
      { header && (
        <div className={styles.sidebarLayoutHeader}>
          {header}
        </div>
      )}
      <div className={styles.sidebarLayoutBody}>
        {mainAndSidebar}
      </div>
    </main>
  );
};

SidebarLayout.Header = defineSlotComponent('header', ({ children }) =>
  <>{children}</>
);
SidebarLayout.Header.displayName = 'SidebarLayout.Header';

SidebarLayout.Main = defineSlotComponent('main', ({ children }) =>
  <div className={styles.sidebarLayoutMain}>
    {children}
  </div>
);
SidebarLayout.Main.displayName = 'SidebarLayout.Main';

SidebarLayout.Sidebar = defineSlotComponent('sidebar', ({ children }) =>
  <aside className={styles.sidebarLayoutSidebar}>
    {children}
  </aside>
);
SidebarLayout.Sidebar.displayName = 'SidebarLayout.Sidebar';

export default SidebarLayout;
